import { EOrderCurrentStatus } from "../../Enum/EOrderCurrentStatus"
import { EProductOrderModeType } from "../../Enum/EProductOrderModeType"
import { CalculatePromotions } from "../Calculates/CalculatePromotions"
import { BaseModel } from "../Generics/BaseModel"
import { converteDateToSql } from "../Generics/Helps"
import { GenericOrderFoodAdditionalEntity } from "./GenericOrderFoodAdditionalEntity"
import { EOrderItemChargeType } from "../../Enum/EOrderItemChargeType"

export abstract class GenericOrderFood extends BaseModel {
  id: number

  valueBase: number

  valueAddtionals: number

  valueDiscount: number

  valuePaid: number

  chargeType: EOrderItemChargeType

  catalogValueSnapshot: number

  rodizioSessionEntityId?: number

  rodizioIncludesAdditionals: boolean

  rodizioParticipantName?: string

  date: string

  description: string

  isReservation: boolean

  reservationDate: string

  reservationTime: string

  employeeEditeId: string

  employeeId: number

  currentStatus: EOrderCurrentStatus

  calculatePromotions?: CalculatePromotions

  addtionals: GenericOrderFoodAdditionalEntity[]

  selections: any[]

  constructor(data: Partial<GenericOrderFood> = {}) {
    super()

    this.id = data.id ?? -1

    this.employeeId = data.employeeId ?? -1

    this.employeeEditeId = data.employeeEditeId ?? ""

    this.valueBase = data.valueBase ?? 0

    this.valueAddtionals = data.valueAddtionals ?? 0

    this.valueDiscount = data.valueDiscount ?? 0

    this.valuePaid = data.valuePaid ?? 0

    this.chargeType = data.chargeType ?? EOrderItemChargeType.Normal

    this.catalogValueSnapshot = data.catalogValueSnapshot ?? this.valueBase

    this.rodizioSessionEntityId = data.rodizioSessionEntityId

    this.rodizioIncludesAdditionals = data.rodizioIncludesAdditionals ?? false

    this.rodizioParticipantName = data.rodizioParticipantName

    this.description = data.description ?? ""

    this.isReservation = data.isReservation ?? false

    this.reservationDate = data.reservationDate ?? ""

    this.reservationTime = data.reservationTime ?? ""

    this.date = data.date ?? converteDateToSql(new Date())

    this.calculatePromotions = data.calculatePromotions

    this.currentStatus = data.currentStatus || EOrderCurrentStatus.Pending

    this.addtionals = this.normalizeAdditionals(data.addtionals)

    this.selections = data.selections ?? (data as any).selectedSelections ?? []
  }

  protected override getJsonFields(): string[] {
    return [
      "id",
      "date",
      "description",
      "isReservation",
      "reservationDate",
      "reservationTime",
      "addtionals",
      "selections",
    ]
  }

  protected override getJsonValue(key: string, value: any): any {
    if (key !== "selections" || !Array.isArray(value)) {
      return value
    }

    return value.map((selection) => ({
      selectionEntityId: selection.selectionEntityId,
      selectionOptionEntityId: selection.selectionOptionEntityId,
    }))
  }

  private normalizeAdditionals(additionals?: GenericOrderFoodAdditionalEntity[]): GenericOrderFoodAdditionalEntity[] {
    if (!additionals || additionals.length === 0) {
      return []
    }

    const groupedAdditionals = new Map<number, GenericOrderFoodAdditionalEntity>()

    additionals.forEach((item) => {
      const current = new GenericOrderFoodAdditionalEntity({ ...item })

      const additionalId = current.addtionalEntityId

      if (additionalId <= 0) {
        return
      }

      const safeAmount = current.amount > 0 ? current.amount : 1

      const additionalBaseValue = Number(current.addtionalEntity?.value ?? 0)

      const unitValue = current.valueUnique > 0 ? current.valueUnique : additionalBaseValue

      if (groupedAdditionals.has(additionalId)) {
        const existing = groupedAdditionals.get(additionalId)!
        existing.amount += safeAmount

        if (existing.valueUnique <= 0 && unitValue > 0) {
          existing.valueUnique = unitValue
        }

        existing.isFree = false
        existing.calculate()
        return
      }

      groupedAdditionals.set(
        additionalId,
        new GenericOrderFoodAdditionalEntity({
          ...current,
          amount: safeAmount,
          isFree: false,
          valueUnique: unitValue,
          valueTotal: safeAmount * unitValue,
        }),
      )
    })

    return Array.from(groupedAdditionals.values())
  }

  isClosed() {
    return this.currentStatus === EOrderCurrentStatus.Closed
  }

  abstract needKitchen(): boolean

  abstract getName(): string

  abstract getImage(): string

  abstract getProductOrderMode(): EProductOrderModeType

  supportsReservation(): boolean {
    return this.getProductOrderMode() !== EProductOrderModeType.ImmediateOnly
  }

  getValueWithAddtions = (): number => {
    return this.valueBase + this.getValueAddtions()
  }

  getValueAddtions = (): number => {
    if (this.calculatePromotions) {
      return this.calculatePromotions.valueAddtionals
    }

    let finalValue: number = 0

    this.addtionals.forEach((e) => {
      finalValue += e.valueUnique * e.amount
    })

    this.selections.forEach((e) => {
      finalValue += Number(e.value ?? 0)
    })

    return finalValue
  }

  override beforeToJSON(): void | Promise<void> {
    this.calculatePromotions = null
  }

  setDescription(ss: string) {
    this.description = ss
  }

  setReservationInfo(isReservation: boolean, reservationDate: string, reservationTime: string) {
    this.isReservation = isReservation
    this.reservationDate = isReservation ? reservationDate : ""
    this.reservationTime = isReservation ? reservationTime : ""
  }
}
